import { createContext, useContext, useEffect, useState } from 'react';
import { useStore } from '@xyflow/react';

const ANGLE = 30;          // label tilt in degrees
const LABEL_HEIGHT = 20;   // badge height (text-xs + py-0.5), in px

// For the slanted edge to appear perfectly vertical in screen space after
// the -ANGLE rotation, the local X inset must equal LABEL_HEIGHT × tan(ANGLE).
//   tipInset = 20 × tan(30°) = 20 / √3 ≈ 11.547 px
const TIP_INSET = LABEL_HEIGHT * Math.tan((ANGLE * Math.PI) / 180);

/**
 * Context for label geometry — kept for future flexibility.
 * Defaults to the mathematically exact values; no Provider needed.
 */
export const LabelStyleCtx = createContext({ tipInset: TIP_INSET, angle: ANGLE });

// ---------------------------------------------------------------------------
// Node shell style helper
// ---------------------------------------------------------------------------

// Palette des nœuds — valeurs alignées sur les tokens du DS « Cosmos »
// (iris / sky-blue / amber / orange / rose / emerald de front_app/src/index.css) :
//   normal / active / glow : anneau de la carte détaillée (inchangés)
//   soft / text            : pastille compacte affichée au dézoom (NodeLodOverlay)
// `restAction` = action ECA REST (appel HTTP sortant) — orange, cf. ecaNodeVisuals.js.
export const NODE_COLORS = {
  event:        { normal: '#A78BFA', active: '#7C3AED', glow: 'rgba(167,139,250,0.3)', soft: '#EDE9FE', text: '#5B21B6' },
  action:       { normal: '#60A5FA', active: '#2563EB', glow: 'rgba(96,165,250,0.3)',  soft: '#DBEAFE', text: '#1E40AF' },
  restAction:   { normal: '#F97316', active: '#C2410C', glow: 'rgba(249,115,22,0.3)',  soft: '#FFEDD5', text: '#9A3412' },
  condition:    { normal: '#FBBF24', active: '#D97706', glow: 'rgba(251,191,36,0.3)',  soft: '#FEF3C7', text: '#92400E' },
  gateway:      { normal: '#FB7185', active: '#E11D48', glow: 'rgba(251,113,133,0.3)', soft: '#FFE4E6', text: '#9F1239' },
  gatewayFired: { normal: '#4ADE80', active: '#16A34A', glow: 'rgba(74,222,128,0.3)',  soft: '#D1FAE5', text: '#047857' },
};

export const nodeShell = (colors, selected) => {
  // Only the SELECTED node's ring scales by 1/zoom (via --rf-inv-zoom) to keep a
  // constant *screen-space* thickness when zoomed out. Unselected nodes use a
  // static ring: making every node's box-shadow depend on the per-frame zoom
  // variable forced a box-shadow repaint of ALL nodes on every zoom frame, which
  // is expensive (blurred shadows) and made zooming feel laggy. With one selected
  // node at a time, the variable now drives a single node's repaint.
  const v = 'var(--rf-inv-zoom, 1)';
  return {
    overflow: 'hidden',
    borderRadius: 8,
    minWidth: 250,
    maxWidth: 280,
    backgroundColor: '#ffffff',
    boxShadow: selected
      ? `0 0 0 calc(4px * ${v}) ${colors.active}, 0 0 0 calc(8px * ${v}) ${colors.glow}, 0 2px 6px rgba(0,0,0,0.10)`
      : `0 0 0 2.5px ${colors.normal}, 0 2px 4px rgba(0,0,0,0.08)`,
  };
};

export const useNodeHover = () => {
  const [hovered, setHovered] = useState(false);
  return {
    hovered,
    hoverProps: {
      onMouseEnter: () => setHovered(true),
      onMouseLeave: () => setHovered(false),
    },
  };
};

// ---------------------------------------------------------------------------
// TopHandleLabels
// ---------------------------------------------------------------------------

export const TopHandleLabels = ({ items, hovered, connectedKeys, className = '' }) => {
  const { tipInset, angle } = useContext(LabelStyleCtx);
  if (!items?.length) return null;
  return items.map((item, i) => {
    const key = item.key ?? item;
    if (!hovered && !connectedKeys?.has(key)) return null;
    return (
      <div
        key={key}
        style={{
          position: 'absolute',
          top: -LABEL_HEIGHT,
          left: `${((i + 1) / (items.length + 1)) * 100}%`,
          transformOrigin: '0 100%',
          transform: `rotate(-${angle}deg)`,
          pointerEvents: 'none',
          zIndex: 50,
          whiteSpace: 'nowrap',
        }}
      >
        {/* Background layer: carries the clipped parallelogram + slant shadow.
            Kept separate from the text so the text is never rasterized through
            a `filter`, which is what made the rotated label look blurry. */}
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            clipPath: `polygon(${tipInset}px 0, 100% 0, 100% 100%, 0 100%)`,
            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
          }}
          className="block bg-emerald-600"
        />
        {/* Text layer: no filter \u2192 crisp glyph rasterization. */}
        <span
          style={{ WebkitFontSmoothing: 'antialiased', textRendering: 'optimizeLegibility' }}
          className={`relative block text-xs text-white px-1.5 py-0.5 ${className}`}
        >
          {'\u00A0\u00A0'}{item.label ?? key}
        </span>
      </div>
    );
  });
};

// ---------------------------------------------------------------------------
// BottomHandleLabels
// ---------------------------------------------------------------------------

export const BottomHandleLabels = ({ items, hovered, connectedKeys, className = '' }) => {
  const { tipInset, angle } = useContext(LabelStyleCtx);
  if (!items?.length) return null;
  return items.map((item, i) => {
    const key = item.key ?? item;
    if (!hovered && !connectedKeys?.has(key)) return null;
    const rightPct = `${((items.length - i) / (items.length + 1)) * 100}%`;
    return (
      <div
        key={key}
        style={{
          position: 'absolute',
          bottom: -LABEL_HEIGHT,
          right: rightPct,
          transformOrigin: '100% 0',
          transform: `rotate(-${angle}deg)`,
          pointerEvents: 'none',
          zIndex: 50,
          whiteSpace: 'nowrap',
        }}
      >
        {/* Background layer: clipped parallelogram + slant shadow (see Top). */}
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            clipPath: `polygon(0 0, 100% 0, calc(100% - ${tipInset}px) 100%, 0 100%)`,
            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
          }}
          className="block bg-emerald-600"
        />
        {/* Text layer: no filter \u2192 crisp glyphs. */}
        <span
          style={{ WebkitFontSmoothing: 'antialiased', textRendering: 'optimizeLegibility' }}
          className={`relative block text-xs text-white px-1.5 py-0.5 ${className}`}
        >
          {item.label ?? key}{'\u00A0\u00A0'}
        </span>
      </div>
    );
  });
};

// ---------------------------------------------------------------------------
// Level-of-detail (LOD) overlay
// ---------------------------------------------------------------------------

/** Below this zoom level the detailed card is unreadable, so nodes collapse
 *  to a solid colored billboard (type colour + label + description). */
export const LOD_THRESHOLD = 0.55;

/**
 * Reports whether the LOD billboard should be shown. The selector returns a
 * BOOLEAN (zoom < threshold), so a node re-renders only when it actually crosses
 * the threshold \u2014 NOT on every zoom frame, and not on pan (transform[0]/[1]).
 *
 * The smooth 1/zoom counter-scale of the billboard is handled out-of-React via
 * the `--rf-inv-zoom` CSS variable published by <ZoomScaleBridge>, so reading a
 * continuous zoom value here (which would re-render every node each frame) is
 * deliberately avoided.
 */
export const useZoomLod = (threshold = LOD_THRESHOLD) =>
  useStore((s) => s.transform[2] < threshold);

/**
 * Single canvas-zoom subscriber. Publishes the clamped inverse zoom (1/zoom) as
 * the `--rf-inv-zoom` CSS custom property on :root, which the LOD billboards use
 * for their counter-scale. This is the ONLY component that re-renders per zoom
 * frame (and it renders null) \u2014 nodes stay untouched. Mount once inside <ReactFlow>.
 */
export const ZoomScaleBridge = () => {
  const inv = useStore((s) => 1 / Math.max(s.transform[2], 0.2));
  useEffect(() => {
    document.documentElement.style.setProperty('--rf-inv-zoom', String(inv));
  }, [inv]);
  return null;
};

/**
 * Pastille compacte affichée au dézoom, par-dessus la carte détaillée (fondu par
 * opacité) :
 *   - fond pastel + liseré de la couleur du type, texte foncé (contraste AA) ;
 *   - une icône déduite du plugin (voir ecaNodeVisuals.getNodeIcon).
 * Seule cette vue compacte est concernée : la carte détaillée est inchangée.
 *
 * La pastille garde le CADRE RECTANGULAIRE du nœud : les handles React Flow sont
 * positionnés sur ce cadre, donc toute autre forme (losange…) décrocherait les
 * connecteurs de la bordure visible.
 *
 * Le libellé et l'icône vivent en *espace local* (px simples) et suivent donc le
 * zoom du canvas. Seul l'anneau de sélection est contre-échelonné par 1/zoom via
 * la variable CSS `--rf-inv-zoom` : le nœud ne re-rend pas à chaque frame de zoom.
 *
 * Pointer events : quand la pastille est active elle capture les clics
 * (pointerEvents:auto) pour éviter d'atteindre les champs cachés dessous — les
 * événements remontent au wrapper React Flow, donc sélection/drag fonctionnent.
 * Inactive, elle est traversante. Le focus n'est pas affecté : la carte détaillée
 * reste montée (opacité 0), un champ focalisé le reste.
 *
 * Seul le libellé est affiché : le type est déjà porté par la couleur et l'icône.
 */
export const NodeLodOverlay = ({ active, colors, title, selected, icon: Icon }) => {
  const v = 'var(--rf-inv-zoom, 1)';

  return (
    <div
      aria-hidden={!active}
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        padding: '10px 14px',
        textAlign: 'center',
        overflow: 'hidden',
        borderRadius: 10,
        background: colors.soft,
        color: colors.text,
        boxShadow: selected
          ? `0 0 0 calc(4px * ${v}) ${colors.active}, 0 0 0 calc(8px * ${v}) ${colors.glow}, 0 2px 6px rgba(0,0,0,0.12)`
          : `0 0 0 2.5px ${colors.normal}, 0 2px 4px rgba(0,0,0,0.10)`,
        opacity: active ? 1 : 0,
        transition: 'opacity 160ms ease',
        pointerEvents: active ? 'auto' : 'none',
      }}
    >
      {/* Décorative : le libellé juste dessous porte déjà l'information. */}
      {Icon && <Icon aria-hidden size={34} strokeWidth={1.75} color={colors.active} style={{ flexShrink: 0 }} />}

      <span style={{
        fontSize: 20, fontWeight: 600, lineHeight: 1.2, wordBreak: 'break-word',
        display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 3, overflow: 'hidden',
      }}>
        {title}
      </span>
    </div>
  );
};
